// src/components/PDF/SplitPDF.js (Updated)
import React, { useState } from 'react';
import { Upload, Download, FileText, Split, List, LayoutGrid, Hash } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { pdfService } from '../../services/pdfService';
import PDFPreviewSave from './PDFPreviewSave';
import JSZip from 'jszip';
import { useAuthGuard } from '../../hooks/useAuthGuard';

const SplitPDF = ({ isLoading, setIsLoading }) => {
  const { requireAuth, AuthModal } = useAuthGuard();
  const [pdfFile, setPdfFile] = useState(null);
  const [splitPages, setSplitPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [showPreviewSave, setShowPreviewSave] = useState(false);

  const [splitMode, setSplitMode] = useState('pages'); 
  const [sectionCount, setSectionCount] = useState(2);
  const [customRange, setCustomRange] = useState('');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles[0]) {
        setPdfFile(acceptedFiles[0]);
        setSplitPages([]);
      }
    }
  });

  const splitPDF = async () => {
    if (!pdfFile) {
      toast.error('Please select a PDF file to split');
      return;
    }

    setIsLoading(true);
    try {
      const pages = await pdfService.splitPDF(pdfFile);
      setSplitPages(pages.map((page, index) => ({
        blob: page,
        name: `${pdfFile.name.replace('.pdf', '')}_page_${index + 1}.pdf`,
        index: index + 1
      })));
      toast.success(`PDF split into ${pages.length} pages!`);
    } catch (error) {
      toast.error('Error splitting PDF');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSplit = async () => {
    if (!pdfFile) return toast.error('Please select a PDF file');

    setIsLoading(true);
    try {
      let results = [];
      
      if (splitMode === 'pages') {
        const pages = await pdfService.splitPDF(pdfFile);
        results = pages.map((page, index) => ({
          blob: page,
          name: `${pdfFile.name.replace('.pdf', '')}_page_${index + 1}.pdf`,
          index: index + 1
        }));
      } 
      else if (splitMode === 'sections') {
        const sections = await pdfService.splitIntoSections(pdfFile, sectionCount);
        results = sections.map((section, index) => ({
          blob: section,
          name: `${pdfFile.name.replace('.pdf', '')}_part_${index + 1}.pdf`,
          index: index + 1
        }));
      }
      else if (splitMode === 'range') {
        if (!customRange) throw new Error("Please enter a range");
        const rangeFiles = await pdfService.splitByRanges(pdfFile, customRange);
        results = rangeFiles.map((item, index) => ({
          blob: item.blob,
          name: `${pdfFile.name.replace('.pdf', '')}_${item.rangeLabel.replace(' ', '_')}.pdf`,
          index: index + 1,
          label: item.rangeLabel
        }));
      }

      setSplitPages(results);
      toast.success('PDF Split complete!');
    } catch (error) {
      toast.error(error.message || 'Error splitting PDF');
    } finally {
      setIsLoading(false);
    }
  };

  const previewPage = (page) => {
    setSelectedPage(page);
    setShowPreviewSave(true);
  };

  const downloadPage = (page) => {
    requireAuth(() => {
      const url = URL.createObjectURL(page.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = page.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  };

  const downloadAll = () => {
    if (splitPages.length === 0) return;

    requireAuth(async () => {
      setIsLoading(true);
      const zip = new JSZip();
      const folder = zip.folder("split_pdfs");

      try {
        // Add each split PDF blob to the zip folder
        splitPages.forEach((page) => {
          folder.file(page.name, page.blob);
        });

        // Generate the zip content
        const zipContent = await zip.generateAsync({ type: 'blob' });

        // Create download link
        const url = URL.createObjectURL(zipContent);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${pdfFile.name.replace('.pdf', '')}_split_files.zip`;
        document.body.appendChild(a);
        a.click();

        // Cleanup
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success('All files bundled into ZIP!');
      } catch (error) {
        console.error('ZIP generation failed:', error);
        toast.error('Failed to create ZIP file');
      } finally {
        setIsLoading(false);
      }
    });
  };

  const resetForm = () => {
    setPdfFile(null);
    setSplitPages([]);
    setSelectedPage(null);
    setShowPreviewSave(false);
  };

  const handleSaveComplete = () => {
    toast.success('Page saved to your document library!');
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Split PDF into Pages</h2>
      
      {/* File Upload Zone */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive 
            ? 'border-[#232751] bg-[#232751]/5' 
            : 'border-gray-300 hover:border-gray-400'
          }
        `}
      >
        <input {...getInputProps()} />
        <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p className="text-sm font-medium text-gray-600 mb-2">
          {isDragActive ? 'Drop PDF here...' : 'Click to select or drag PDF here'}
        </p>
        <p className="text-xs text-gray-500">
          Upload a PDF to split into individual pages
        </p>
      </div>

      {/* Selected File */}
      {pdfFile && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <FileText className="w-6 h-6 text-red-500" />
            <div>
              <p className="font-medium text-gray-700">{pdfFile.name}</p>
              <p className="text-sm text-gray-500">
                {(pdfFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
        </div>
      )}

      {pdfFile && (
        <div className="bg-white border rounded-xl p-6 shadow-sm space-y-6">
          <div className="flex items-center space-x-3 border-b pb-4">
            <FileText className="w-6 h-6 text-red-500" />
            <span className="font-medium text-gray-700 truncate">{pdfFile.name}</span>
          </div>

          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">Select Splitting Method</label>
            <div className="flex p-1 bg-gray-100 rounded-xl">
              {[
                { id: 'pages', label: 'All Pages', icon: List },
                { id: 'sections', label: 'Equal Parts', icon: LayoutGrid },
                { id: 'range', label: 'Custom Range', icon: Hash },
              ].map((mode) => (
                <button
                  key={mode.id}
                  onClick={() => setSplitMode(mode.id)}
                  className={`flex-1 flex items-center justify-center py-2.5 rounded-lg text-sm font-medium transition-all ${
                    splitMode === mode.id ? 'bg-white shadow text-[#232751]' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <mode.icon className="w-4 h-4 mr-2" />
                  {mode.label}
                </button>
              ))}
            </div>

            {/* Conditional Inputs */}
            <div className="pt-2">
              {splitMode === 'sections' && (
                <div className="animate-in fade-in slide-in-from-top-2">
                  <p className="text-xs text-gray-500 mb-2">Split the document into equal page chunks.</p>
                  <input
                    type="number"
                    min="2"
                    value={sectionCount}
                    onChange={(e) => setSectionCount(parseInt(e.target.value))}
                    className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#232751] outline-none"
                    placeholder="Number of parts..."
                  />
                </div>
              )}

              {splitMode === 'range' && (
                <div className="animate-in fade-in slide-in-from-top-2">
                  <p className="text-xs text-gray-500 mb-2">Example: 1-3, 5, 10-12 (Creates 3 separate PDFs)</p>
                  <input
                    type="text"
                    value={customRange}
                    onChange={(e) => setCustomRange(e.target.value)}
                    className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#232751] outline-none"
                    placeholder="e.g. 1-5, 8, 11-15"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Action Button */}
        <button
          onClick={handleSplit}
          disabled={!pdfFile || isLoading}
          className="w-full bg-[#232751] text-white py-4 rounded-xl font-bold hover:bg-[#1b1f43] transition-all disabled:opacity-50 flex items-center justify-center"
        >
          <Split className="w-5 h-5 mr-2" />
          {splitMode === 'pages' && 'Split into Single Pages'}
          {splitMode === 'sections' && `Split into ${sectionCount} Parts`}
          {splitMode === 'range' && 'Extract Specified Ranges'}
        </button>
        
        {pdfFile && (
          <button
            onClick={resetForm}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* Split Results */}
      {splitPages.length > 0 && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">
              Generated Files ({splitPages.length})
            </h3>
            <button
              onClick={downloadAll}
              disabled={isLoading}
              className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center shadow-sm disabled:opacity-50"
            >
              {isLoading ? (
                <span className="flex items-center">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Zipping...
                </span>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download All (.zip)
                </>
              )}
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
            {splitPages.map((page) => (
              <div 
                key={page.index} 
                className="bg-white border border-gray-200 rounded-xl p-4 hover:border-[#232751]/40 hover:shadow-md transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-red-50 rounded-lg group-hover:bg-red-100 transition-colors">
                      <FileText className="w-6 h-6 text-red-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-gray-800 truncate">
                        {/* Shows "Pages 1-5", "Part 1", or "Page 1" based on the mode used */}
                        {page.label || (splitMode === 'sections' ? `Part ${page.index}` : `Page ${page.index}`)}
                      </p>
                      <p className="text-[10px] text-gray-400 truncate mt-0.5" title={page.name}>
                        {page.name}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => previewPage(page)}
                    className="flex-1 bg-gray-50 text-[#232751] hover:bg-[#232751]/5 px-3 py-2 rounded-lg text-xs font-semibold border border-gray-100 hover:border-[#232751]/20 transition-all"
                  >
                    Preview & Save
                  </button>
                  <button
                    onClick={() => downloadPage(page)}
                    className="p-2 bg-gray-50 text-green-600 hover:bg-green-100 rounded-lg transition-all border border-gray-100 hover:border-green-200"
                    title="Download specific file"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {AuthModal}

      {/* Preview/Save Modal */}
      <PDFPreviewSave
        isOpen={showPreviewSave}
        onClose={() => setShowPreviewSave(false)}
        pdfBlob={selectedPage?.blob}
        fileName={selectedPage?.name}
        operationType="split"
        onSaveComplete={handleSaveComplete}
      />
    </div>
  );
};

export default SplitPDF;
